<template>
  <dialog ref="dialogRef" class="review-dialog" @close="emit('close')" @click="handleBackdropClick">
    <div class="review-card">
      <div class="review-title">{{ localize('handshake.unknownReview.title') }}</div>
      <p class="review-lead">{{ localize('handshake.unknownReview.lead') }}</p>

      <div class="review-list">
        <template v-if="editing">
          <div class="review-item">
            <div class="review-meta">
              {{ metaOf(editing.handshake) }}
            </div>
            <div class="review-positions">
              <HandshakePositionMark
                v-for="position in editing.positions"
                :key="position"
                :position-index="position"
                :photos="photosAt(editing.handshake, position)"
                align="start"
                show-number
                @open-photo="emit('openPhoto', position, $event, editing.handshake.id)"
              />
            </div>

            <input
              v-model="name"
              class="review-input"
              type="text"
              :maxlength="STAND_IN_NAME_MAX_LENGTH"
              :placeholder="localize('handshake.unknownReview.namePlaceholder')"
              :aria-label="localize('handshake.unknownReview.namePlaceholder')"
              autocomplete="off"
            />

            <div v-if="photoPosition !== null" class="review-field">
              <HandshakePhotoTile
                :label="
                  photo
                    ? localizeWithValues('handshake.unknownReview.photoCount', ['1'])
                    : localize('handshake.photo.add')
                "
                :disabled="saving"
                @click="pickPhoto"
              />
            </div>
          </div>
        </template>

        <template v-else-if="candidates.length">
          <div v-for="candidate in candidates" :key="candidate.handshake.id" class="review-item">
            <div class="review-meta">
              {{ metaOf(candidate.handshake) }}
            </div>
            <div class="review-positions">
              <HandshakePositionMark
                v-for="position in candidate.positions"
                :key="position"
                :position-index="position"
                :photos="photosAt(candidate.handshake, position)"
                align="start"
                show-number
                @open-photo="emit('openPhoto', position, $event, candidate.handshake.id)"
              />
            </div>
            <div class="review-actions">
              <button
                type="button"
                class="review-button"
                :disabled="saving"
                @click="confirming = candidate.handshake.id"
              >
                {{ localize('handshake.unknownReview.notUnique') }}
              </button>
              <button type="button" class="review-button is-unique" :disabled="saving" @click="startEditing(candidate)">
                {{ localize('handshake.unknownReview.unique') }}
              </button>
            </div>
          </div>
        </template>

        <p v-else class="review-empty">{{ localize('handshake.unknownReview.empty') }}</p>
      </div>

      <div class="review-ctas">
        <template v-if="editing">
          <div
            class="cta-circle"
            :aria-label="localize('handshake.unknownReview.back')"
            @click="!saving && stopEditing()"
          >
            <GamIcon :name="GamIconName.ARROW_LEFT" :size="GamIconSize.LARGE" is-current />
          </div>
          <div
            class="cta-circle cta-check"
            :class="{ 'is-active': canSave || saving }"
            :aria-label="localize('handshake.unknownReview.save')"
            @click="saveUnique"
          >
            <GamIcon v-if="!saving" :name="GamIconName.CHECK" :size="GamIconSize.X_LARGE" is-current />
            <div v-else class="cta-spinner" />
          </div>
        </template>
        <div v-else class="cta-circle" @click="emit('close')">
          <GamIcon :name="GamIconName.X_CLOSE" :size="GamIconSize.X_LARGE" is-current />
        </div>
      </div>
    </div>

    <GamConfirmModal
      v-if="confirming"
      :title="localize('handshake.unknownReview.confirm.title')"
      :keep-label="localize('handshake.unknownReview.confirm.yes')"
      :destructive-label="localize('handshake.unknownReview.confirm.cancel')"
      @close="confirming = null"
      @keep="saveNotUnique"
      @confirm="confirming = null"
    >
      <p class="review-confirm">{{ localize('handshake.unknownReview.confirm.body') }}</p>
    </GamConfirmModal>
  </dialog>
</template>

<script lang="ts" setup>
import type { HandshakeDto } from '@/core/data/handshake/handshake.interface';
import { StandInVerdict } from '@/core/data/handshake/handshake.type';
import { gambits, localize, localizeWithValues } from '@/core/gambits';
import { getShortDate } from '@/core/helpers/date.helper';
import GamConfirmModal from '@/views/components/GamConfirmModal.vue';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamIconName, GamIconSize } from '@/views/composables/constants/components/gamIcon.constants';
import { useHandshakePhotoUpload } from '@/views/composables/hooks/useHandshakePhotoUpload';
import HandshakePhotoTile from '@/views/main/handshakes/HandshakePhotoTile.vue';
import HandshakePositionMark from '@/views/main/handshakes/HandshakePositionMark.vue';
import type { StandInCandidate } from '@/views/main/handshakes/utils/position-collection';
import { useFileDialog } from '@vueuse/core';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useToast } from 'vue-toastification';

/** The API's limit on a stand-in's name. */
const STAND_IN_NAME_MAX_LENGTH = 24;

defineProps<{
  candidates: StandInCandidate[];
}>();

const emit = defineEmits<{
  close: [];
  decided: [handshake: HandshakeDto];
  openPhoto: [position: number, hash: string, handshakeId: string];
}>();

const toast = useToast();
const { upload } = useHandshakePhotoUpload();
const { open: openFile, onChange: onFile } = useFileDialog({ accept: '.jpg, .jpeg, .png', multiple: false });

const dialogRef = ref<HTMLDialogElement>();
const editing = ref<StandInCandidate | null>(null);
const confirming = ref<string | null>(null);
const name = ref('');
const photo = ref<File | null>(null);
const saving = ref(false);

const canSave = computed(() => !saving.value && name.value.trim().length > 0);

/**
 * A photo can be added here only to a handshake of one position that has none.
 * With several, where it lands would decide the pink without showing it; the
 * Positions tab is where each square can be seen.
 */
const photoPosition = computed(() => {
  const candidate = editing.value;
  if (candidate?.positions.length !== 1) return null;

  const [position] = candidate.positions;
  return photosAt(candidate.handshake, position).length ? null : position;
});

onMounted(() => {
  dialogRef.value?.showModal();
});

onBeforeUnmount(() => {
  if (dialogRef.value?.open) dialogRef.value.close();
});

const handleBackdropClick = (event: MouseEvent) => {
  if (event.target === dialogRef.value) emit('close');
};

onFile((files) => {
  const file = files?.[0];
  if (!file || !editing.value || photoPosition.value == null) return;

  photo.value = file;
});

/** This handshake's photos of one position, newest first, as the rank's side square takes them. */
function photosAt(handshake: HandshakeDto, position: number) {
  return (handshake.photos ?? [])
    .filter((item) => item.positionIndex === position)
    .sort((left, right) => (right.createdAt ?? '').localeCompare(left.createdAt ?? ''))
    .map((item) => ({ hash: item.imageBlobHash, handshakeId: handshake.id }));
}

function metaOf(handshake: HandshakeDto): string {
  const who = localize(handshake.isExMember ? 'handshake.standIn.exPair.unnamed' : 'handshake.passerby.unnamed');
  const date = getShortDate(handshake.createdAt);
  return [who, date, handshake.location?.name].filter(Boolean).join(' ・ ');
}

function startEditing(candidate: StandInCandidate) {
  editing.value = candidate;
  name.value = '';
  photo.value = null;
}

function stopEditing() {
  editing.value = null;
  photo.value = null;
}

function pickPhoto() {
  openFile();
}

async function saveUnique() {
  const candidate = editing.value;
  if (!candidate || !canSave.value) return;

  saving.value = true;
  const result = await gambits.userService.setStandInVerdict(
    candidate.handshake.id,
    StandInVerdict.UNIQUE,
    name.value.trim(),
  );
  saving.value = false;

  if (result.error || !result.data) {
    toast.error(localize('handshake.unknownReview.failed'));
    return;
  }

  const file = photo.value;
  const position = photoPosition.value;
  toast.success(localizeWithValues('handshake.unknownReview.saved', [result.data.standInName ?? name.value.trim()]));
  stopEditing();
  emit('decided', result.data);
  if (file && position != null) void upload(candidate.handshake.id, file, position);
}

async function saveNotUnique() {
  const handshakeId = confirming.value;
  if (!handshakeId || saving.value) return;

  saving.value = true;
  const result = await gambits.userService.setStandInVerdict(handshakeId, StandInVerdict.NOT_UNIQUE);
  saving.value = false;
  confirming.value = null;

  if (result.error || !result.data) {
    toast.error(localize('handshake.unknownReview.failed'));
    return;
  }

  emit('decided', result.data);
}
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;

.review-dialog {
  width: 100%;
  height: 100%;
  border: 0;
  padding: var(--sai-top) 1rem var(--sai-bottom);
  margin: 0;
  max-width: 100%;
  max-height: 100%;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
  overflow: hidden;

  &::backdrop {
    background: rgba(23, 23, 23, 0.75);
  }
}

.review-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: min(100%, 420px);
  max-height: min(80vh, 720px);
  padding: var(--spacing-xxl) var(--spacing-xl);
  padding-bottom: 6.5rem;
  background: var(--color-dark-700);
  border: 1px solid var(--color-white-10);
  border-radius: var(--radius-large);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
}

.review-title {
  font-size: 20px;
  font-weight: 700;
  text-align: center;
  color: var(--color-white);
}

.review-lead {
  margin: var(--spacing-s) 0 var(--spacing-xl);
  font-size: 13px;
  text-align: center;
  color: var(--color-white-50);
}

.review-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xl);
  overflow-y: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.review-item {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  padding-bottom: var(--spacing-xl);
  border-bottom: 1px solid var(--color-white-10);

  &:last-child {
    border-bottom: 0;
    padding-bottom: 0;
  }
}

.review-meta {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: var(--color-white-50);
}

.review-positions {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.review-field {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.review-input {
  padding: 0.7rem 0.9rem;
  border: 1px solid var(--color-white-10);
  border-radius: 0.75rem;
  background: var(--color-dark-600);
  color: var(--color-white);
  font: inherit;
  font-size: 15px;
  outline: none;

  &:focus {
    border-color: var(--color-white-30);
  }
}

.review-actions {
  display: flex;
  gap: var(--spacing-md);
}

.review-button {
  flex: 1;
  padding: 0.7rem 1rem;
  border: 1px solid var(--color-white-10);
  border-radius: 2rem;
  background: none;
  color: var(--color-white-70);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &.is-unique {
    border-color: transparent;
    background: var(--color-reported);
    color: var(--color-white);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  @include press;
}

.review-empty,
.review-confirm {
  margin: 0;
  font-size: 13px;
  text-align: center;
  color: var(--color-white-50);
}

/* The same round buttons as adding a spot. */
.review-ctas {
  position: absolute;
  bottom: var(--safe-bottom-offset);
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 12px;
}

.cta-circle {
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 3.5rem;
  border: 1px solid var(--color-white-10);
  backdrop-filter: blur(8px);
  background-color: var(--color-dark-5);
  color: var(--color-white-70);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 200ms linear;

  :deep(.icon-arrow-left) {
    transform: scale(0.85);
  }

  @include hover {
    background-color: var(--color-dark-20);
    border-color: var(--color-white-30);
    color: var(--color-white);
  }

  &.cta-check:not(.is-active) {
    opacity: 0.4;
    pointer-events: none;
  }

  &.cta-check.is-active {
    background-color: var(--color-pink);
    color: var(--color-white);

    @include hover {
      filter: brightness(1.1);
    }
  }

  @include press;
}

.cta-spinner {
  width: 1.25rem;
  height: 1.25rem;
  border: 2px solid var(--color-white-30);
  border-top-color: var(--color-white);
  border-radius: 50%;
  animation: spin 600ms linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
